import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useAppStore = defineStore('app', () => {
  // State
  const isLoading = ref(false)
  const isSidebarOpen = ref(true)
  const notifications = ref([])
  const dashboardStats = ref({
    total_campaigns: 0,
    total_contacts: 0,
    emails_sent: 0,
    active_automations: 0,
    draft_campaigns: 0
  })
  const adminStats = ref({
    inactive_users: 0,
    system_alerts: 0
  })
  
  // Getters
  const unreadNotifications = computed(() => 
    Array.isArray(notifications.value) ? notifications.value.filter(n => !n.read) : []
  )
  
  // Actions
  const setLoading = (loading) => {
    isLoading.value = loading
  }
  
  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value
  }
  
  const setSidebarOpen = (open) => {
    isSidebarOpen.value = open
  }
  
  const closeSidebar = () => {
    isSidebarOpen.value = false
  }
  
  const addNotification = (notification) => {
    // Ensure notifications is an array
    if (!Array.isArray(notifications.value)) {
      notifications.value = []
    }
    
    notifications.value.unshift({
      id: Date.now(),
      read: false,
      timestamp: new Date(),
      ...notification
    })
  }
  
  const markNotificationRead = (id) => {
    if (Array.isArray(notifications.value)) {
      const notification = notifications.value.find(n => n.id === id)
      if (notification) {
        notification.read = true
      }
    }
  }
  
  const markAllNotificationsRead = () => {
    if (Array.isArray(notifications.value)) {
      notifications.value.forEach(n => n.read = true)
    }
  }
  
  const removeNotification = (id) => {
    try {
      if (Array.isArray(notifications.value)) {
        const index = notifications.value.findIndex(n => n && n.id === id)
        if (index > -1) {
          notifications.value.splice(index, 1)
        }
      }
    } catch (error) {
      console.error('Error removing notification:', error)
    }
  }
  
  const dismissNotification = (id) => {
    if (!id) {
      console.warn('Cannot dismiss notification: no ID provided')
      return
    }
    removeNotification(id)
  }
  
  const clearAllNotifications = () => {
    notifications.value = []
  }
  
  const updateDashboardStats = (stats) => {
    dashboardStats.value = { ...dashboardStats.value, ...stats }
  }
  
  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get('/dashboard/stats')
      dashboardStats.value = response.data
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error)
    }
  }
  
  const fetchNotifications = async () => {
    try {
      const response = await axios.get('/notifications')
      // Ensure we always set an array
      let notificationData = []
      if (Array.isArray(response.data)) {
        notificationData = response.data
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        notificationData = response.data.data
      } else {
        console.warn('Unexpected notifications response format:', response.data)
        notifications.value = []
        return
      }

      // Transform Laravel notifications to our frontend format
      notifications.value = notificationData.map(notification => ({
        id: notification.id,
        type: notification.data?.type || 'info',
        title: notification.data?.title || 'Notification',
        message: notification.data?.message || '',
        titleKey: notification.data?.title_key,
        messageKey: notification.data?.message_key,
        messageParams: notification.data?.message_params,
        campaignId: notification.data?.campaign_id,
        route: notification.data?.campaign_id
          ? { name: 'campaigns.show', params: { id: notification.data.campaign_id } }
          : null,
        read: notification.read_at !== null,
        timestamp: notification.created_at
      }))
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      // Clear notifications on fetch error to prevent showing invalid data
      notifications.value = []
    }
  }
  
  const addTestNotification = (type = 'info') => {
    const testNotifications = {
      success: { title: 'Success!', message: 'Operation completed successfully.' },
      warning: { title: 'Warning', message: 'Please review this action.' },
      error: { title: 'Error', message: 'Something went wrong.' },
      info: { title: 'Information', message: 'Here is some useful information.' }
    }

    addNotification({
      type,
      ...testNotifications[type] || testNotifications.info
    })
  }

  // Campaign notification methods
  const addCampaignStartedNotification = (campaignName, recipientCount, campaignId = null) => {
    addNotification({
      type: 'campaign_started',
      titleKey: 'common.notifications.campaign.started_title',
      messageKey: 'common.notifications.campaign.started_message',
      messageParams: { name: campaignName, count: recipientCount },
      title: `Campaign Started`,
      message: `Campaign "${campaignName}" has started sending to ${recipientCount} recipients`,
      campaignId,
      route: campaignId ? { name: 'campaigns.show', params: { id: campaignId } } : null
    })
  }

  const addCampaignCompletedNotification = (campaignName, sentCount, failedCount, campaignId = null) => {
    addNotification({
      type: 'campaign_completed',
      titleKey: 'common.notifications.campaign.completed_title',
      messageKey: 'common.notifications.campaign.completed_message',
      messageParams: { name: campaignName, sent: sentCount, failed: failedCount },
      title: `Campaign Completed`,
      message: `Campaign "${campaignName}" completed: ${sentCount} sent, ${failedCount} failed`,
      campaignId,
      route: campaignId ? { name: 'campaigns.show', params: { id: campaignId } } : null
    })
  }

  const addCampaignFailedNotification = (campaignName, errorMessage = '', campaignId = null) => {
    addNotification({
      type: 'campaign_failed',
      titleKey: 'common.notifications.campaign.failed_title',
      messageKey: 'common.notifications.campaign.failed_message',
      messageParams: { name: campaignName },
      title: `Campaign Failed`,
      message: `Campaign "${campaignName}" failed to send${errorMessage ? `: ${errorMessage}` : ''}`,
      campaignId,
      route: campaignId ? { name: 'campaigns.show', params: { id: campaignId } } : null
    })
  }

  const addCampaignScheduledNotification = (campaignName, scheduledDate, campaignId = null) => {
    addNotification({
      type: 'success',
      titleKey: 'common.notifications.campaign.scheduled_title',
      messageKey: 'common.notifications.campaign.scheduled_message',
      messageParams: { name: campaignName, date: scheduledDate },
      title: `Campaign Scheduled`,
      message: `Campaign "${campaignName}" scheduled for ${scheduledDate}`,
      campaignId,
      route: campaignId ? { name: 'campaigns.show', params: { id: campaignId } } : null
    })
  }

  const addCampaignProgressNotification = (campaignName, sentCount, totalCount, campaignId = null) => {
    const percent = totalCount > 0 ? Math.round((sentCount / totalCount) * 100) : 0
    addNotification({
      type: 'campaign_progress',
      titleKey: 'common.notifications.campaign.progress_title',
      messageKey: 'common.notifications.campaign.progress_message',
      messageParams: { name: campaignName, sent: sentCount, total: totalCount, percent },
      title: `Campaign Progress`,
      message: `Campaign "${campaignName}": ${sentCount}/${totalCount} emails sent (${percent}%)`,
      campaignId,
      route: campaignId ? { name: 'campaigns.show', params: { id: campaignId } } : null
    })
  }

  return {
    // State
    isLoading,
    isSidebarOpen,
    notifications,
    dashboardStats,
    adminStats,
    
    // Getters
    unreadNotifications,
    
    // Actions
    setLoading,
    toggleSidebar,
    setSidebarOpen,
    closeSidebar,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    removeNotification,
    dismissNotification,
    clearAllNotifications,
    updateDashboardStats,
    fetchDashboardStats,
    fetchNotifications,
    addTestNotification,
    // Campaign notifications
    addCampaignStartedNotification,
    addCampaignCompletedNotification,
    addCampaignFailedNotification,
    addCampaignScheduledNotification,
    addCampaignProgressNotification
  }
})